import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtPct, gainClass } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy, Medal, Award, BadgeCheck, TrendingUp, Users, ShieldCheck, FileUp, BarChart3, X } from "lucide-react";

const RANK_ICON = { 1: Trophy, 2: Medal, 3: Award };
const RANK_COLOR = { 1: "text-yellow-500", 2: "text-gray-400", 3: "text-orange-500" };

const TIER = {
  broker_verified: { label: "Broker ✓", cls: "bg-emerald-100 text-emerald-700", Icon: ShieldCheck },
  imported: { label: "Imported", cls: "bg-blue-100 text-blue-700", Icon: FileUp },
  unverified: { label: "Unverified", cls: "bg-gray-100 text-gray-500", Icon: null },
};

export const TierBadge = ({ tier, userId }) => {
  const t = TIER[tier] || TIER.unverified;
  return (
    <span data-testid={`tier-badge-${userId}`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${t.cls}`}>
      {t.Icon && <t.Icon size={10} />} {t.label}
    </span>
  );
};

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [influenceRows, setInfluenceRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [influenceLoading, setInfluenceLoading] = useState(true);
  const [statsFor, setStatsFor] = useState(null);

  useEffect(() => {
    api.get("/leaderboard")
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    api.get("/leaderboard/influence")
      .then(({ data }) => setInfluenceRows(data))
      .catch(() => setInfluenceRows([]))
      .finally(() => setInfluenceLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="leaderboard-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Leaderboard</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2">Top traders</h1>
        <p className="text-gray-500 mt-2">Two ways to rise: personal PnL or the wealth you create for your copiers.</p>
      </div>

      <Tabs defaultValue="pnl" className="w-full" data-testid="leaderboard-tabs">
        <TabsList className="bg-gray-100 h-11">
          <TabsTrigger value="pnl" data-testid="tab-pnl" className="gap-2 px-4">
            <TrendingUp size={14} />
            Personal PnL
          </TabsTrigger>
          <TabsTrigger value="influence" data-testid="tab-influence" className="gap-2 px-4">
            <Users size={14} />
            Influence
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pnl" className="mt-6">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">No traders yet.</p>
          ) : (
            <Card className="border border-gray-200 rounded-2xl overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
                <div className="col-span-1">Rank</div>
                <div className="col-span-5">Trader</div>
                <div className="col-span-2 text-right">PnL</div>
                <div className="col-span-2 text-right">Win rate</div>
                <div className="col-span-2 text-right">Trades</div>
              </div>
              <div className="divide-y divide-gray-100">
                {rows.map((r, i) => {
                  const rank = i + 1;
                  const RankIcon = RANK_ICON[rank];
                  return (
                    <Link
                      key={r.user_id}
                      to={`/profile/${r.user_id}`}
                      data-testid={`leaderboard-row-${rank}`}
                      className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="col-span-1 flex items-center gap-2">
                        {RankIcon ? (
                          <RankIcon className={RANK_COLOR[rank]} size={20} />
                        ) : (
                          <span className="text-sm font-mono text-gray-400">{rank}</span>
                        )}
                      </div>
                      <div className="col-span-11 md:col-span-5 flex items-center gap-3">
                        <Avatar className={`h-10 w-10 border ${rank <= 3 ? "border-2 " + RANK_COLOR[rank].replace("text-", "border-") : "border-gray-200"}`}>
                          <AvatarImage src={r.avatar_url} alt={r.name} />
                          <AvatarFallback>{r.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium flex items-center gap-1.5 flex-wrap">
                            {r.name}
                            {r.verified_pro && (
                              <BadgeCheck
                                size={14}
                                className="text-gain"
                                data-testid={`verified-badge-${r.user_id}`}
                                title="Verified Pro"
                              />
                            )}
                            <TierBadge tier={r.verification_tier} userId={r.user_id} />
                            <button
                              data-testid={`trader-stats-btn-${r.user_id}`}
                              title="Risk-adjusted stats"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStatsFor(r); }}
                              className="text-gray-400 hover:text-brand transition-colors"
                            >
                              <BarChart3 size={13} />
                            </button>
                          </p>
                          <p className="text-xs text-gray-500">Portfolio {fmtUSD(r.portfolio_value)}</p>
                        </div>
                      </div>
                      <div className={`col-span-6 md:col-span-2 text-right ${gainClass(r.total_pnl)}`}>
                        <p className="font-mono tabular-nums font-medium">{fmtUSD(r.total_pnl)}</p>
                        <p className="text-xs tabular-nums">{fmtPct(r.total_pnl_pct)}</p>
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right">
                        <p className="font-mono tabular-nums">{r.win_rate.toFixed(0)}%</p>
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right">
                        <p className="font-mono tabular-nums">{r.trades_count}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="influence" className="mt-6">
          <p className="text-sm text-gray-500 mb-4" data-testid="influence-blurb">
            Ranked by aggregate PnL earned by everyone copying this trader. Pro trader material.
          </p>
          {influenceLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : influenceRows.length === 0 ? (
            <Card className="border border-dashed border-gray-200 rounded-2xl p-10 text-center" data-testid="influence-empty">
              <Users className="mx-auto text-gray-300" size={32} />
              <p className="font-display text-xl font-medium mt-3">No copiers yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Once traders start copying each other, this board fills up automatically.
              </p>
            </Card>
          ) : (
            <Card className="border border-gray-200 rounded-2xl overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
                <div className="col-span-1">Rank</div>
                <div className="col-span-5">Trader</div>
                <div className="col-span-2 text-right">Copier PnL</div>
                <div className="col-span-2 text-right">Copiers</div>
                <div className="col-span-2 text-right">Mirrored</div>
              </div>
              <div className="divide-y divide-gray-100">
                {influenceRows.map((r, i) => {
                  const rank = i + 1;
                  const RankIcon = RANK_ICON[rank];
                  return (
                    <Link
                      key={r.user_id}
                      to={`/profile/${r.user_id}`}
                      data-testid={`influence-row-${rank}`}
                      className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="col-span-1 flex items-center gap-2">
                        {RankIcon ? (
                          <RankIcon className={RANK_COLOR[rank]} size={20} />
                        ) : (
                          <span className="text-sm font-mono text-gray-400">{rank}</span>
                        )}
                      </div>
                      <div className="col-span-11 md:col-span-5 flex items-center gap-3">
                        <Avatar className={`h-10 w-10 border ${rank <= 3 ? "border-2 " + RANK_COLOR[rank].replace("text-", "border-") : "border-gray-200"}`}>
                          <AvatarImage src={r.avatar_url} alt={r.name} />
                          <AvatarFallback>{r.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium flex items-center gap-1">
                            {r.name}
                            {r.verified_pro && (
                              <BadgeCheck size={14} className="text-gain" title="Verified Pro" />
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            {fmtUSD(r.realized_pnl)} realized · {fmtUSD(r.unrealized_pnl)} open
                          </p>
                        </div>
                      </div>
                      <div className={`col-span-6 md:col-span-2 text-right ${gainClass(r.copier_pnl)}`}>
                        <p className="font-mono tabular-nums font-medium">{fmtUSD(r.copier_pnl)}</p>
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right">
                        <p className="font-mono tabular-nums">{r.copier_count}</p>
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right">
                        <p className="font-mono tabular-nums text-gray-500">{r.mirrored_trades_count}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {statsFor && <TraderStatsDialog trader={statsFor} onClose={() => setStatsFor(null)} />}
    </div>
  );
}

function TraderStatsDialog({ trader, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/users/${trader.user_id}/verified-stats`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [trader.user_id]);
  const m = data?.metrics;
  const Stat = ({ label, value, cls = "" }) => (
    <div className="rounded-xl bg-gray-50 p-2.5 text-center">
      <p className="text-[10px] text-gray-400">{label}</p>
      <p className={`text-sm font-semibold ${cls}`}>{value}</p>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="trader-stats-dialog">
      <Card className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold flex items-center gap-2">{trader.name} <TierBadge tier={data?.tier || trader.verification_tier} userId={trader.user_id} /></p>
            <p className="text-xs text-gray-500 mt-0.5" data-testid="verification-source">{data?.verification_source || ""}</p>
          </div>
          <button onClick={onClose} data-testid="trader-stats-close" className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        {data === null ? (
          <p className="text-sm text-gray-400 mt-4">Loading…</p>
        ) : data === false ? (
          <p className="text-sm text-gray-400 mt-4">Stats unavailable.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mt-4" data-testid="trader-stats-grid">
              <Stat label="Return" value={`${m.total_return_pct >= 0 ? "+" : ""}${m.total_return_pct}%`} cls={gainClass(m.total_return_pct)} />
              <Stat label="Realized P&L" value={fmtUSD(m.realized_pnl)} cls={gainClass(m.realized_pnl)} />
              <Stat label="Win rate" value={`${m.win_rate}%`} />
              <Stat label="Profit factor" value={m.profit_factor} />
              <Stat label="Max drawdown" value={`-${m.max_drawdown_pct}%`} />
              <Stat label="Expectancy" value={fmtUSD(m.expectancy)} />
              <Stat label="Sharpe" value={m.sharpe ?? "n/a"} />
              <Stat label="Sortino" value={m.sortino === null ? "n/a" : m.sortino} />
              <Stat label="Trades" value={m.trades_count} />
            </div>
            <p className="text-[11px] text-gray-400 mt-3">
              Fills: {data.counts.platform} platform · {data.counts.imported} imported · {data.counts.broker} broker-synced.
              {data.broker_metrics
                ? ` Broker-verified subset: ${data.broker_metrics.win_rate}% win rate over ${data.broker_metrics.closed_trades} closed trades.`
                : " No broker-synchronized fills yet — stats are platform-computed, never self-reported."}
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
